import React, { useState, useMemo } from 'react';
import { useMutation } from 'react-query';
import { Form } from '@kuzmycz/react-form-ula';
import { Modal } from '../../../../Components/uielements';
import BioPlusTextField from '../../../../Components/form-fields/BioPlusTextField';
import {
  ModalButtonWrapper,
  ModalGap,
  OutlineButton,
} from '../../../../services/settings/appStyle/global.style';
import * as CompanyService from '../../../../services/bioplus-services/company';
import { IntlMessages } from '../../../../helper/langHelper';
import { ModalWrapper, SectionTitle, SubmitButton } from '../company.style';
import { EyeButton, PasswordWrapper } from './Resetpasswordmodal';
import { ClosedEye, OpenEye } from '../../../../Components/icons';
import styled from 'styled-components';
import { media } from '../../../../helper/styleHelper';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../hooks/AuthContext';
import { WarningCard } from '../../../../Components/uielements/warningCard';
import { Error } from '@mui/icons-material';

const ErrorText = styled.p`
  margin: 0;
  margin-top: 8px;
  font-size: 13px;
  color: #f87171;
`;

const PasswordRequirements = styled.p`
  margin: 8px 0 0 0;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PhoneFieldWrapper = styled.div`
  position: relative;
  width: 100%;

  /* Push the real input text to the right so it starts after the prefix */
  input {
    padding-left: ${({ $prefixWidth }) => `${$prefixWidth}px`} !important;
  }
`;

const CountryCodePrefix = styled.span`
  position: absolute;
  left: 0;
  bottom: 0;
  height: 100%;
  display: flex;
  align-items: center;
  padding-left: 4px;
  font-size: 14px;
  color: #fff;
  pointer-events: none;
  user-select: none;
  z-index: 1;

  ${media.xxl`
    font-size:16px
  `}
`;

const HelpText = styled.p`
  margin: 8px 0 0 0;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
`;

// Environment-based country config — same as AccountTransferModal
const COUNTRY_CONFIG = {
  au_stage: { code: '+61', digits: 9, country: 'Australia' },
  au_production: { code: '+61', digits: 9, country: 'Australia' },
  production: { code: '+1', digits: 10, country: 'USA' },
  default: { code: '+61', digits: 9, country: 'Australia' },
};

const getCountryConfig = (env) => COUNTRY_CONFIG[env] || COUNTRY_CONFIG.default;

// Rough width estimate (in px) for the prefix block so the input text never
// overlaps it, regardless of which country code is active.
const getPrefixWidth = (code) => code.length * 9 + 5;

const UpdateContactDetails = ({ onClose, data, environment = 'default' }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [contactNumberError, setContactNumberError] = useState('');

  const countryConfig = useMemo(() => getCountryConfig(environment), [environment]);
  const prefixWidth = useMemo(() => getPrefixWidth(countryConfig.code), [countryConfig.code]);

  const mutation = useMutation((values) => CompanyService.updateContactInfo(values), {
    onSuccess: async () => {
      alert('Contact details updated successfully.');
      onClose();
      logout();
      navigate('/');
      // logout
    },
    onError: (error) => {
      alert(
        error?.response?.data?.message ||
          error?.message ||
          'Something went wrong while updating contact details.'
      );
    },
  });

  const validatePhoneNumber = (phone) => {
    const digitsOnly = (phone || '').replace(/\D/g, '');
    return digitsOnly.length === countryConfig.digits;
  };

  const validateFields = (values) => {
    let isValid = true;

    if (!values?.userData?.email) {
      setEmailError('Email is required');
      isValid = false;
    } else {
      setEmailError('');
    }

    const phone = values?.userData?.contactNumber;
    if (!phone) {
      setContactNumberError('Contact number is required');
      isValid = false;
    } else if (!validatePhoneNumber(phone)) {
      setContactNumberError(
        `Phone number must contain exactly ${countryConfig.digits} digits for ${countryConfig.country}.`
      );
      isValid = false;
    } else {
      setContactNumberError('');
    }

    const password = values?.userData?.password;
    let passwordErrorMessage = '';

    if (!password) {
      passwordErrorMessage = 'Password is required';
    } else if (password.length < 6) {
      passwordErrorMessage = 'Password must be at least 6 characters long.';
    } else {
      const alphabeticCount = (password.match(/[a-zA-Z]/g) || []).length;
      const numericCount = (password.match(/[0-9]/g) || []).length;
      const specialCharCount = (password.match(/[^a-zA-Z0-9]/g) || []).length;

      if (alphabeticCount < 4) {
        passwordErrorMessage = 'Password must contain at least 4 alphabetic characters.';
      } else if (numericCount < 1) {
        passwordErrorMessage = 'Password must contain at least 1 numeric character.';
      } else if (specialCharCount < 1) {
        passwordErrorMessage = 'Password must contain at least 1 special character.';
      }
    }

    setPasswordError(passwordErrorMessage);
    if (passwordErrorMessage) {
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = (values) => {
    if (!validateFields(values)) {
      return;
    }

    const phoneDigitsOnly = (values?.userData?.contactNumber || '').replace(/\D/g, '');
    const contactNumber = `${countryConfig.code}${phoneDigitsOnly}`;

    const value = {
      email: values?.userData?.email,
      contactNumber,
      password: values?.userData?.password,
    };

    mutation.mutate(value);
  };

  const validator = () => undefined;

  // Strip non-digits so only the LOCAL portion of any pasted/typed value
  // ever ends up in the field. Also enforces the per-country digit cap.
  const sanitizePhoneInput = (rawValue) =>
    (rawValue || '').replace(/\D/g, '').slice(0, countryConfig.digits);

  // Prevent any non-digit keystrokes from reaching the input at all.
  const handlePhoneKeyDown = (event) => {
    const allowedControlKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Home',
      'End',
    ];
    if (allowedControlKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handlePhonePaste = (event) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    const digitsOnly = sanitizePhoneInput(pasted);
    const formCtx = event.currentTarget;
    // Trigger a native input-like change so the form library picks it up.
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    ).set;
    nativeInputValueSetter.call(formCtx, digitsOnly);
    formCtx.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const formatedData = {
    ...data,
    userData: {
      email: data?.userData?.email || '',
      // strip any existing country code / non-digits so the input only shows local digits
      contactNumber: (data?.userData?.phone || '').replace(/\D/g, '').slice(-countryConfig.digits),
      password: '',
    },
  };

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        title={<IntlMessages id="text.update.contact.details" />}
      >
        <Form initialValues={formatedData} validator={validator} onSubmit={handleSubmit}>
          <ModalWrapper>
            <ModalGap>
              <SectionTitle>Contact Details</SectionTitle>

              <InputWrapper>
                <IntlMessages id="label.email">
                  {(label) => (
                    <BioPlusTextField label={label || 'Email'} name="userData.email" fullWidth />
                  )}
                </IntlMessages>
                {emailError && <ErrorText>{emailError}</ErrorText>}
              </InputWrapper>

              <InputWrapper>
                <PhoneFieldWrapper $prefixWidth={prefixWidth}>
                  <CountryCodePrefix>{countryConfig.code}</CountryCodePrefix>
                  <IntlMessages id="input.label.phone.no">
                    {(label) => (
                      <BioPlusTextField
                        label={label || 'Contact Number'}
                        name="userData.contactNumber"
                        type="tel"
                        fullWidth
                        autoComplete="off"
                        inputMode="numeric"
                        maxLength={countryConfig.digits}
                        onKeyDown={handlePhoneKeyDown}
                        onPaste={handlePhonePaste}
                        parse={sanitizePhoneInput}
                        format={sanitizePhoneInput}
                      />
                    )}
                  </IntlMessages>
                </PhoneFieldWrapper>
                <HelpText>
                  {countryConfig.digits} digits required for {countryConfig.country}
                </HelpText>
                {contactNumberError && <ErrorText>{contactNumberError}</ErrorText>}
              </InputWrapper>

              <InputWrapper>
                <PasswordWrapper>
                  <IntlMessages id="label.password">
                    {(label) => (
                      <BioPlusTextField
                        label={label || 'Password'}
                        name="userData.password"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                      />
                    )}
                  </IntlMessages>
                  <EyeButton
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label="Toggle current password visibility"
                  >
                    {showPassword ? <OpenEye /> : <ClosedEye />}
                  </EyeButton>
                </PasswordWrapper>
                {passwordError && <ErrorText>{passwordError}</ErrorText>}
              </InputWrapper>

              <PasswordRequirements>
                Minimum 6 characters · 4 letters · 1 numbers · 1 special characters
              </PasswordRequirements>
            </ModalGap>

            <WarningCard
              title="Important"
              message="Please check your new Email carefully before proceeding with the updation process. Once the update process is complete, this action cannot be undone. You may lose your account if the email is not correct!"
              icon={<Error />}
            />

            <ModalButtonWrapper style={{ paddingBottom: '20px' }}>
              <OutlineButton onClick={onClose}>
                <IntlMessages id="modal.button.cancel" />
              </OutlineButton>
              <SubmitButton varient="normal" type="submit">
                {mutation.isLoading && <span class="loader" style={{ marginRight: '4px' }}></span>}
                <IntlMessages id="modal.button.submit" />
              </SubmitButton>
            </ModalButtonWrapper>
          </ModalWrapper>
        </Form>
      </Modal>
    </>
  );
};

export default UpdateContactDetails;
